// ============================================================================
// 📅 MANPOWER ATTENDANCE SERVICE - FINAL VERSION
// Path: services/manpowerAttendance.service.ts
// 
// FIX: Correctly counts unique employees, not duplicate assignments
// ============================================================================

import { 
  collection, 
  doc, 
  query, 
  where,
  getDocs,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

// ============================================================================
// INTERFACES
// ============================================================================

export interface AttendanceRecord {
  date: string;
  status: 'Present' | 'Absent' | 'Late' | 'Incomplete' | 'Early Leave';
  checkInTime?: string;
  checkOutTime?: string;
  location?: string;
  locationName?: string;  
  latitude?: number;
  longitude?: number;
}

export interface EmployeeAttendanceSummary {
  empCode: string;
  empNumber: string;
  employeeName: string;
  employeeUUID?: string;
  pin?: string;
  totalDays: number;
  presentDays: number;
  absentDays: number;
  attendanceRecords: AttendanceRecord[];
  attendancePercentage: number;
}

export interface AttendanceDateRange {
  startDate: string;
  endDate: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================
const DUBAI_TIMEZONE = 'Asia/Dubai';

// ============================================================================
// MANPOWER ATTENDANCE SERVICE
// ============================================================================

class ManpowerAttendanceService {
  private readonly ATTENDANCE_PATH = 'Attendance_Records/PTSEmployees/Records';
  private readonly EMPLOYEES_COLLECTION = 'employees';

  // Cache for PIN to UUID mapping
  private pinToUuidCache: Map<string, string> | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // ========================================================================
  // HELPER: Format Dubai Time
  // ========================================================================
  private formatDubaiTime(timestamp: any): string {
    if (!timestamp) return '';
    
    try {
      let date: Date;
      
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return '';
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: DUBAI_TIMEZONE
      });
    } catch {
      return '';
    }
  }

  // ========================================================================
  // HELPER: Get Employee Identifiers (PIN variations)
  // ========================================================================
  private getEmployeeIdentifiers(pin: string): string[] {
    const identifiers = new Set<string>();
    
    if (!pin) return [];
    
    identifiers.add(pin);
    const cleanPin = pin.replace(/^EMP/i, '');
    identifiers.add(cleanPin);
    identifiers.add(`EMP${cleanPin}`);
    
    if (/^\d+$/.test(cleanPin)) {
      identifiers.add(cleanPin.padStart(4, '0'));
      identifiers.add(`EMP${cleanPin.padStart(4, '0')}`);
    }
    
    identifiers.add(pin.toLowerCase());
    identifiers.add(pin.toUpperCase());
    identifiers.add(cleanPin.toLowerCase());
    identifiers.add(cleanPin.toUpperCase());
    
    return Array.from(identifiers);
  }

  // ========================================================================
  // HELPER: Clean Location Name
  // ========================================================================
  private cleanLocationName(location: string): string {
    if (!location) return '';
    
    let cleaned = location.trim();
    cleaned = cleaned.replace(/^location\s+/i, '');
    cleaned = cleaned
      .replace(/\s*\(Polygon\s+Boundary\)\s*/gi, '')
      .replace(/\s*polygon\s+boundary\s*/gi, '');
    
    return cleaned;
  }

  // ========================================================================
  // Build PIN to UUID mapping from employees collection
  // ========================================================================
  private async buildPinToUuidMap(): Promise<Map<string, string>> {
    // Check cache
    if (this.pinToUuidCache && (Date.now() - this.cacheTimestamp) < this.CACHE_TTL) {
      return this.pinToUuidCache;
    }
    
    const pinToUuid = new Map<string, string>();
    
    try {
      const employeesRef = collection(db, this.EMPLOYEES_COLLECTION);
      const snapshot = await getDocs(employeesRef);
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const uuid = doc.id;
        const pin = data.pin;
        
        if (pin && uuid) {
          const identifiers = this.getEmployeeIdentifiers(pin);
          identifiers.forEach(id => {
            pinToUuid.set(id.toLowerCase(), uuid);
          });
        }
      });
      
      // Cache it
      this.pinToUuidCache = pinToUuid;
      this.cacheTimestamp = Date.now();
      
    } catch (error) {
      console.error('Error building PIN to UUID map:', error);
    }
    
    return pinToUuid;
  }

  // ========================================================================
  // Get attendance for a specific date
  // ========================================================================
  async getAttendanceForDate(
    employees: { empCode: string; employeeName: string }[],
    date: string
  ): Promise<Map<string, AttendanceRecord>> {
    const attendanceMap = new Map<string, AttendanceRecord>();

    try {
      // Step 1: Build PIN to UUID mapping
      const pinToUuid = await this.buildPinToUuidMap();
      
      // Step 2: Query ALL attendance records for this date
      const recordsRef = collection(db, this.ATTENDANCE_PATH);
      const dateQuery = query(
        recordsRef,
        where('date', '==', date)
      );

      const snapshot = await getDocs(dateQuery);
      
      console.log(`📊 Found ${snapshot.size} attendance records for ${date}`);

      // Step 3: Build a map of attendance records by UUID
      const attendanceByUuid = new Map<string, any>();

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const docId = doc.id;
        
        // Extract UUID from document ID
        let uuid = '';
        
        if (docId.includes('-')) {
          const parts = docId.split('-');
          if (parts.length >= 5) {
            uuid = parts.slice(0, 5).join('-');
          } else {
            uuid = parts[0];
          }
        } else {
          uuid = docId;
        }
        
        if (uuid) {
          attendanceByUuid.set(uuid.toLowerCase(), {
            docId,
            data
          });
        }
      });

      console.log(`📋 Built attendance UUID map with ${attendanceByUuid.size} entries`);

      // Step 4: Match employees to attendance records via UUID
      // Track unique employees for accurate counting
      const uniqueMatched = new Set<string>();
      const uniqueUnmatched = new Set<string>();

      for (const emp of employees) {
        // Get UUID for this employee's PIN
        const empIdentifiers = this.getEmployeeIdentifiers(emp.empCode);
        let employeeUuid: string | undefined;
        
        for (const id of empIdentifiers) {
          const uuid = pinToUuid.get(id.toLowerCase());
          if (uuid) {
            employeeUuid = uuid;
            break;
          }
        }

        if (!employeeUuid) {
          uniqueUnmatched.add(emp.empCode);
          attendanceMap.set(emp.empCode, { date, status: 'Absent' });
          continue;
        }

        // Look up attendance by UUID
        const attendanceRecord = attendanceByUuid.get(employeeUuid.toLowerCase());

        if (attendanceRecord) {
          uniqueMatched.add(emp.empCode);
          const data = attendanceRecord.data;
          
          // Determine status
          let status: AttendanceRecord['status'] = 'Present';
          
          if (data.status) {
            status = data.status;
          } else if (data.checkIn && !data.checkOut) {
            const now = new Date();
            const today = now.toISOString().split('T')[0];
            status = date < today ? 'Incomplete' : 'Present';
          }

          const record: AttendanceRecord = {
            date,
            status,
            checkInTime: this.formatDubaiTime(data.checkIn) || '',
            checkOutTime: this.formatDubaiTime(data.checkOut) || '',
            location: data.location || data.checkInLocation || '',
            locationName: this.cleanLocationName(
              data.checkInLocationName || data.locationName || data.location || ''
            ),
            latitude: data.latitude || data.checkInLatitude || 0,
            longitude: data.longitude || data.checkInLongitude || 0
          };

          attendanceMap.set(emp.empCode, record);
        } else {
          uniqueUnmatched.add(emp.empCode);
          attendanceMap.set(emp.empCode, { date, status: 'Absent' });
        }
      }

      // Log unique counts (not duplicate assignment counts)
      const uniqueEmployeeCount = new Set(employees.map(e => e.empCode)).size;
      console.log(`✅ Matched ${uniqueMatched.size} unique employees with attendance (${uniqueEmployeeCount} unique in list)`);

    } catch (error) {
      console.error('Error fetching attendance for date:', error);
      
      employees.forEach(emp => {
        attendanceMap.set(emp.empCode, { date, status: 'Absent' });
      });
    }

    return attendanceMap;
  }

  /**
   * Get employee UUID from employee collection using pin
   */
  async getEmployeeUUIDByPin(pin: string): Promise<string | null> {
    try {
      const employeesRef = collection(db, this.EMPLOYEES_COLLECTION);
      const q = query(employeesRef, where('pin', '==', pin));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      return querySnapshot.docs[0].id;
    } catch (error) {
      console.error(`Error getting employee UUID for pin ${pin}:`, error);
      return null;
    }
  }

  /**
   * Get employee UUID from employee collection using empCode
   */
  async getEmployeeUUIDByEmpCode(empCode: string): Promise<{ uuid: string | null, pin: string | null }> {
    try {
      const pin = empCode.replace(/^EMP/i, '');
      const uuid = await this.getEmployeeUUIDByPin(pin);
      return { uuid, pin };
    } catch (error) {
      console.error(`Error getting employee UUID for empCode ${empCode}:`, error);
      return { uuid: null, pin: null };
    }
  }

  /**
   * Generate date range array
   */
  private generateDateRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    while (start <= end) {
      dates.push(start.toISOString().split('T')[0]);
      start.setDate(start.getDate() + 1);
    }

    return dates;
  }

  /**
   * Format date to YYYY-MM-DD
   */
  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Get attendance records for a single employee
   */
  async getEmployeeAttendance(
    empCode: string,
    employeeName: string,
    dateRange: AttendanceDateRange
  ): Promise<EmployeeAttendanceSummary> {
    const summary: EmployeeAttendanceSummary = {
      empCode,
      empNumber: empCode,
      employeeName,
      totalDays: 0,
      presentDays: 0,
      absentDays: 0,
      attendanceRecords: [],
      attendancePercentage: 0
    };

    try {
      const { uuid, pin } = await this.getEmployeeUUIDByEmpCode(empCode);
      
      if (!uuid) {
        return summary;
      }

      summary.employeeUUID = uuid;
      summary.pin = pin || undefined;

      const dates = this.generateDateRange(dateRange.startDate, dateRange.endDate);
      summary.totalDays = dates.length;

      const attendancePromises = dates.map(async (date) => {
        const dayAttendance = await this.getAttendanceForDate(
          [{ empCode, employeeName }],
          date
        );
        return dayAttendance.get(empCode) || { date, status: 'Absent' as const };
      });

      const records = await Promise.all(attendancePromises);
      summary.attendanceRecords = records;

      summary.presentDays = records.filter(r => 
        ['Present', 'Late', 'Early Leave', 'Incomplete'].includes(r.status)
      ).length;
      summary.absentDays = records.filter(r => r.status === 'Absent').length;
      summary.attendancePercentage = summary.totalDays > 0 
        ? Math.round((summary.presentDays / summary.totalDays) * 100) 
        : 0;

    } catch (error) {
      console.error(`Error getting attendance for ${empCode}:`, error);
    }

    return summary;
  }

  /**
   * Get attendance for multiple employees
   */
  async getBulkEmployeeAttendance(
    employees: { empCode: string; employeeName: string }[],
    dateRange: AttendanceDateRange
  ): Promise<Map<string, EmployeeAttendanceSummary>> {
    const attendanceMap = new Map<string, EmployeeAttendanceSummary>();

    const attendancePromises = employees.map(async (emp) => {
      const summary = await this.getEmployeeAttendance(emp.empCode, emp.employeeName, dateRange);
      return { empCode: emp.empCode, summary };
    });

    const results = await Promise.all(attendancePromises);

    results.forEach(({ empCode, summary }) => {
      attendanceMap.set(empCode, summary);
    });

    return attendanceMap;
  }

  /**
   * Get today's date in YYYY-MM-DD format
   */
  getTodayDate(): string {
    return this.formatDate(new Date());
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.pinToUuidCache = null;
    this.cacheTimestamp = 0;
  }
}

export const manpowerAttendanceService = new ManpowerAttendanceService();